import { isKnownItemType, itemAnswerSchemas } from '@sciolingo/api-client'
import type { CachedItem } from '../db/types'

/**
 * Local evaluation, per §5.3. It is provisional; the server's result governs.
 *
 * What this function may produce: correct / incorrect / unknown, and by
 * extension a raw score. What it must never produce, anywhere in this file or
 * any other client file: a **percentile**, or a plan update. The student seeing
 * a number that later changes is what destroys trust in the scoring, so the
 * only percentile in the system arrives from the server.
 */
export type LocalResult = 'correct' | 'incorrect' | 'unknown'

export function evaluateLocally(item: CachedItem, answer: unknown): LocalResult {
  if (!isKnownItemType(item.type)) return 'unknown'
  if (item.correctAnswer === null || item.correctAnswer === undefined) return 'unknown'

  const schema = itemAnswerSchemas[item.type]
  const given = schema.safeParse(answer)
  const expected = schema.safeParse(item.correctAnswer)
  if (!given.success || !expected.success) return 'unknown'

  return deepEqual(normalise(given.data), normalise(expected.data)) ? 'correct' : 'incorrect'
}

/** §5.3: a raw score is allowed offline, provided it is labelled provisional. */
export function provisionalRawScore(results: LocalResult[]): { rawScore: number; scored: number } {
  return {
    rawScore: results.filter((r) => r === 'correct').length,
    scored: results.filter((r) => r !== 'unknown').length,
  }
}

function normalise(value: unknown): unknown {
  if (Array.isArray(value)) return [...value].map(normalise).sort(compare)
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => [k, normalise(v)]),
    )
  }
  return value
}

function compare(a: unknown, b: unknown): number {
  return String(a).localeCompare(String(b))
}

function deepEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}
